import React, { useState, useMemo } from 'react';
import { DollarSign, Printer, XCircle, Plus, Trash2, Send, Mail } from 'lucide-react';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import api from '../../services/api.js';
import { handleImprimirCotizacion } from '../../utils/printCotizacion';

export default function ModalCotizacion({ solicitud, onClose, onUpdate }) {
  const [items, setItems] = useState(
    solicitud.items?.map(i => ({ id: i.id, tipo_documento: i.tipo_documento, valor: i.valor || 0 })) || []
  );
  const [extras, setExtras] = useState(solicitud.cotizacion_extras || []);
  const [observaciones, setObservaciones] = useState(solicitud.observaciones_cotizacion || '');
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [resending, setResending] = useState(false);

  const hasExistingQuote = Boolean(
    solicitud.total_cotizacion && Number(solicitud.total_cotizacion) > 0
  );

  const handlePrint = () => {
    handleImprimirCotizacion(solicitud, items, extras, total, observaciones);
  };

  const total = useMemo(() => {
    const sumItems = items.reduce((acc, i) => acc + (parseInt(i.valor) || 0), 0);
    const sumExtras = extras.reduce((acc, e) => acc + (parseInt(e.valor) || 0), 0);
    return sumItems + sumExtras;
  }, [items, extras]);

  const handleItemValueChange = (id, val) => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, valor: val } : i));
  };

  const addExtra = () => setExtras(prev => [...prev, { descripcion: '', valor: 0 }]);
  const removeExtra = (idx) => setExtras(prev => prev.filter((_, i) => i !== idx));
  const handleExtraChange = (idx, field, val) => {
    setExtras(prev => prev.map((e, i) => i === idx ? { ...e, [field]: val } : e));
  };

  const saveCotizacion = async (silent = false) => {
    setSaving(true);
    try {
      await api.post(`/solicitudes/${solicitud.id}/cotizacion`, {
        items,
        extras,
        observaciones,
        total
      });
      if (!silent) toast.success('Cotización guardada.');
      onUpdate(solicitud.id);
      return true;
    } catch {
      toast.error('Error al guardar la cotización.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async () => {
    const ok = await saveCotizacion(true);
    if (!ok) return;

    setSending(true);
    try {
      await api.post(`/solicitudes/${solicitud.id}/enviar-cotizacion`);
      toast.success('Cotización enviada al ciudadano.');
      onUpdate(solicitud.id);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error al enviar cotización.');
    } finally {
      setSending(false);
    }
  };

  const handleResend = async () => {
    const emailDestino = solicitud.email || 'el solicitante';
    const result = await Swal.fire({
      title: '¿Reenviar correo de cotización?',
      html: `Se enviará nuevamente el correo de cotización con los datos de pago al solicitante:<br/><strong style="color: #0284c7; font-size: 1.05rem; display: block; margin-top: 8px;">${emailDestino}</strong>`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#0284c7',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Sí, reenviar correo',
      cancelButtonText: 'Cancelar'
    });

    if (!result.isConfirmed) return;

    const ok = await saveCotizacion(true);
    if (!ok) return;

    setResending(true);
    try {
      await api.post(`/solicitudes/${solicitud.id}/enviar-cotizacion`);
      toast.success(`Correo de cotización reenviado exitosamente a ${emailDestino}.`);
      onUpdate(solicitud.id);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error al reenviar cotización.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: 700, width: '90%' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 style={{ fontSize: '1.2rem', display: 'flex', alignItems: 'center', gap: 8 }}>
            <DollarSign size={20} color="var(--success)" /> Generación de Cotización
          </h2>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            {hasExistingQuote && (
              <button 
                type="button"
                className="btn btn-sm no-print" 
                style={{ 
                  background: '#0284c7', 
                  color: '#ffffff', 
                  border: 'none', 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '6px',
                  fontWeight: 600,
                  padding: '6px 12px',
                  borderRadius: 6,
                  cursor: resending ? 'not-allowed' : 'pointer'
                }}
                onClick={handleResend} 
                disabled={resending || saving || sending}
                title={`Reenviar correo de cotización a ${solicitud.email || 'el ciudadano'}`}
              >
                {resending ? <span className="spinner" style={{ width: 14, height: 14 }} /> : <Mail size={15} />}
                Reenviar Correo
              </button>
            )}
            <button className="btn btn-secondary btn-sm no-print" onClick={handlePrint} title="Imprimir Cotización">
              <Printer size={16} /> Imprimir
            </button>
            <button className="modal-close" onClick={onClose}><XCircle size={18} /></button>
          </div>
        </div>

        <div style={{ maxHeight: '70vh', overflowY: 'auto', paddingRight: 8 }}>
          <div className="alert alert-info no-print" style={{ marginBottom: '1.5rem', fontSize: '0.85rem' }}>
            Estás cotizando la solicitud <strong>{solicitud.numero_seguimiento}</strong> para <strong>{solicitud.nombre_solicitante}</strong>.
          </div>

          <div className="print-section">
            <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--primary-dark)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ background: 'var(--primary)', color: 'white', width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem' }}>1</span>
              Valores por Documento
            </h4>
            
            <div style={{ background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', marginBottom: '2rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 180px', padding: '12px 16px', background: '#f1f5f9', borderBottom: '1px solid #e2e8f0', fontSize: '0.75rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                <div>Documento</div>
                <div style={{ textAlign: 'right' }}>Precio ($)</div>
              </div>
              <div style={{ padding: '8px 16px' }}>
                {items.map(item => (
                  <div key={item.id} className={(!item.valor || item.valor <= 0) ? 'no-print' : ''} style={{ display: 'grid', gridTemplateColumns: '1fr 180px', gap: '16px', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                    <div style={{ fontSize: '0.9rem', color: '#334155', fontWeight: '500' }}>{item.tipo_documento}</div>
                    <div>
                      <div style={{ position: 'relative' }}>
                        <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '0.9rem' }}>$</span>
                        <input
                          type="number"
                          value={item.valor}
                          onChange={e => handleItemValueChange(item.id, e.target.value)}
                          style={{ 
                            width: '100%', padding: '10px 12px 10px 28px', 
                            borderRadius: '8px', border: '1px solid #cbd5e1', 
                            fontSize: '0.95rem', fontWeight: '600', color: '#0f172a',
                            boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.02)',
                            transition: 'all 0.2s', outline: 'none'
                          }}
                          onFocus={e => e.target.style.borderColor = 'var(--primary)'}
                          onBlur={e => e.target.style.borderColor = '#cbd5e1'}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="no-print" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h4 style={{ fontSize: '1rem', fontWeight: '600', margin: 0, color: 'var(--primary-dark)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ background: 'var(--primary)', color: 'white', width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem' }}>2</span>
                Cobros Extras / Especiales
              </h4>
              <button onClick={addExtra} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', fontSize: '0.8rem', fontWeight: '600', color: 'var(--primary)', background: '#eff6ff', border: 'none', borderRadius: '6px', cursor: 'pointer', transition: 'background 0.2s' }} onMouseOver={e => e.currentTarget.style.background = '#dbeafe'} onMouseOut={e => e.currentTarget.style.background = '#eff6ff'}>
                <Plus size={14} strokeWidth={3} /> Añadir Fila
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '2rem' }}>
              {extras.length === 0 && <p className="no-print" style={{ fontSize: '0.9rem', color: '#94a3b8', fontStyle: 'italic', padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px dashed #cbd5e1', textAlign: 'center' }}>No hay cobros extras definidos. Pulsa "Añadir Fila" si lo necesitas.</p>}
              {extras.map((ex, idx) => (
                <div key={idx} className={(!ex.valor || ex.valor <= 0) ? 'no-print' : ''} style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <input
                    type="text"
                    placeholder="Descripción del cobro (ej: Certificación Digital)"
                    value={ex.descripcion}
                    onChange={e => handleExtraChange(idx, 'descripcion', e.target.value)}
                    style={{ flex: 1, padding: '10px 14px', fontSize: '0.9rem', borderRadius: '8px', border: '1px solid #cbd5e1', boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.02)', outline: 'none' }}
                    onFocus={e => e.target.style.borderColor = 'var(--primary)'}
                    onBlur={e => e.target.style.borderColor = '#cbd5e1'}
                  />
                  <div style={{ position: 'relative', width: '160px' }}>
                    <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '0.9rem' }}>$</span>
                    <input
                      type="number"
                      placeholder="Valor"
                      value={ex.valor}
                      onChange={e => handleExtraChange(idx, 'valor', e.target.value)}
                      style={{ width: '100%', padding: '10px 12px 10px 28px', fontSize: '0.95rem', fontWeight: '600', color: '#0f172a', borderRadius: '8px', border: '1px solid #cbd5e1', boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.02)', outline: 'none' }}
                      onFocus={e => e.target.style.borderColor = 'var(--primary)'}
                      onBlur={e => e.target.style.borderColor = '#cbd5e1'}
                    />
                  </div>
                  <button className="icon-btn no-print" onClick={() => removeExtra(idx)} style={{ color: '#ef4444', padding: '8px', borderRadius: '6px', background: '#fef2f2', transition: 'all 0.2s' }} onMouseOver={e => e.currentTarget.style.background = '#fee2e2'} onMouseOut={e => e.currentTarget.style.background = '#fef2f2'} title="Eliminar fila">
                    <Trash2 size={18} />
                  </button>
                </div>
              ))}
            </div>

            <div style={{
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
              padding: '1.5rem',
              borderRadius: '12px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              boxShadow: '0 10px 15px -3px rgba(15, 23, 42, 0.1), 0 4px 6px -4px rgba(15, 23, 42, 0.1)'
            }}>
              <span style={{ fontWeight: 700, fontSize: '1.1rem', color: '#94a3b8', letterSpacing: '0.05em' }}>TOTAL COTIZACIÓN</span>
              <span style={{ fontWeight: 800, fontSize: '2rem', color: '#fff', textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                ${new Intl.NumberFormat('es-CL').format(total)}
              </span>
            </div>
          </div>

          <div className="no-print">
            <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--primary-dark)', marginTop: '2rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ background: 'var(--primary)', color: 'white', width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem' }}>3</span>
              Observaciones Adicionales
            </h4>
            <textarea
              rows={3}
              value={observaciones}
              onChange={e => setObservaciones(e.target.value)}
              placeholder="Nota opcional que aparecerá en el correo de notificación..."
              style={{ width: '100%', marginBottom: '1.5rem', padding: '12px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95rem', outline: 'none', resize: 'vertical' }}
              onFocus={e => e.target.style.borderColor = 'var(--primary)'}
              onBlur={e => e.target.style.borderColor = '#cbd5e1'}
            />
          </div>
        </div>

        <div className="modal-footer no-print" style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.6rem', marginTop: '1.25rem', borderTop: '1px solid #e2e8f0', paddingTop: '1rem', alignItems: 'center' }}>
          <button 
            className="btn btn-secondary btn-sm" 
            onClick={onClose} 
            disabled={saving || sending}
            style={{ padding: '6px 14px', fontSize: '0.85rem', borderRadius: '6px', fontWeight: '600', color: '#64748b', background: '#f1f5f9', border: 'none', transition: 'all 0.2s' }}
            onMouseOver={e => !saving && !sending && (e.currentTarget.style.background = '#e2e8f0')}
            onMouseOut={e => !saving && !sending && (e.currentTarget.style.background = '#f1f5f9')}
          >
            Cancelar
          </button>
          
          <button 
            onClick={() => saveCotizacion()} 
            disabled={saving || sending} 
            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 14px', fontSize: '0.85rem', borderRadius: '6px', fontWeight: '600', color: 'var(--primary)', background: '#eff6ff', border: '1px solid #bfdbfe', transition: 'all 0.2s', cursor: 'pointer' }}
            onMouseOver={e => !saving && !sending && (e.currentTarget.style.background = '#dbeafe')}
            onMouseOut={e => !saving && !sending && (e.currentTarget.style.background = '#eff6ff')}
          >
            {saving ? <span className="spinner" style={{ width: 14, height: 14, borderColor: 'var(--primary) transparent var(--primary) transparent' }} /> : 'Solo Guardar'}
          </button>

          {hasExistingQuote && (
            <button 
              type="button"
              className="btn btn-sm btn-outline"
              onClick={handleResend} 
              disabled={saving || sending || resending}
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '6px', 
                color: '#0284c7', 
                borderColor: '#7dd3fc', 
                background: '#f0f9ff',
                fontWeight: 600, 
                padding: '6px 14px', 
                fontSize: '0.85rem',
                borderRadius: '6px' 
              }}
              title={`Reenviar correo de cotización a ${solicitud.email || 'el ciudadano'}`}
            >
              {resending ? <span className="spinner spinner-sm" style={{ width: 14, height: 14 }} /> : <Mail size={15} />}
              Reenviar Correo
            </button>
          )}

          <button 
            className="btn btn-primary btn-sm" 
            onClick={handleSend} 
            disabled={saving || sending || resending}
            style={{ padding: '6px 16px', fontSize: '0.85rem', borderRadius: '6px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px', boxShadow: '0 2px 4px -1px rgba(37, 99, 235, 0.2)' }}
          >
            {sending ? <span className="spinner" style={{ width: 14, height: 14 }} /> : <><Send size={15} /> Enviar Cotización</>}
          </button>
        </div>
      </div>
    </div>
  );
}
